/* Pulsetto subscription cancellation flow — controller / orchestrator.

   The page is split into editable Shopify sections, one per step, each marked
   with [data-cancel-step="<id>"]. This controller (the `subscription-cancel`
   section) owns ALL logic: it holds the API config + opaque token, runs
   validation on load, shows/hides one step at a time, gates the step-1 button,
   and routes branches.

   Security model (unchanged): the page holds NO secrets and never calls Loop
   directly. It POSTs the opaque URL token verbatim to n8n webhooks:
     POST <validateUrl> { token } -> { ok, subscription }
     POST <cancelUrl>   { token, reason, reasonLabel } -> { ok }
     POST <acceptUrl>   { token, offer, reason, reasonLabel } -> { ok }   (optional retention offer)
   `reason`/`reasonLabel` carry the answer chosen on the reason step so the
   backend can tie each outcome to why the user was leaving (null if skipped).
   The token is never parsed or trusted client-side; no subscription id is sent.

   Step sections talk to the controller purely through data attributes:
     [data-cancel-step="intro|reason|offer-*|confirm|result"]   step wrapper
     [data-cancel-action="begin|continue-reason|keep|decline|confirm-cancel|retry|return"]
     [data-cancel-nav="back"]                                   back one step
     [data-result-target="<variant>"]   on keep/confirm buttons -> result variant
     [data-result-variant="<variant>"]  inside the result step  -> shown container
     [data-accept-id="<id>"]             offer id sent to acceptUrl
     [data-offer-target="offer-*"]       on reason radios        -> branch target
     [data-cancel-checking] / [data-cancel-invalid] / [data-cancel-error]
                                          intro state notes (toggled by controller)
     [data-sub-field="plan|status|price|nextBilling|total"]
                                          filled from the validate response
*/
(function () {
  'use strict';

  var CURRENCY_FORMATTERS = {};
  function fmtMoney(amount, currency) {
    if (amount == null || amount === '') return '';
    var num = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(num)) return '';
    var key = currency || 'USD';
    if (!CURRENCY_FORMATTERS[key]) {
      try {
        CURRENCY_FORMATTERS[key] = new Intl.NumberFormat(undefined, { style: 'currency', currency: key });
      } catch (_) { CURRENCY_FORMATTERS[key] = null; }
    }
    var f = CURRENCY_FORMATTERS[key];
    return f ? f.format(num) : num.toFixed(2) + ' ' + key;
  }

  // Allow http(s), relative paths, and custom-scheme deep links; block dangerous schemes.
  function safeReturnUrl(raw) {
    if (!raw) return null;
    var t = String(raw).trim();
    if (!t) return null;
    var l = t.toLowerCase();
    if (l.indexOf('javascript:') === 0 || l.indexOf('data:') === 0 ||
        l.indexOf('vbscript:') === 0 || l.indexOf('file:') === 0) return null;
    if (/^https?:\/\//i.test(t)) return t;
    if (/^[a-z][a-z0-9+\-.]*:/i.test(t)) return t;
    if (t.charAt(0) === '/') return t;
    return null;
  }

  // True on phones/tablets, where the app deep link can actually open the app.
  // iPadOS 13+ reports a desktop Safari UA, so fall back to touch detection.
  function isMobileDevice() {
    var ua = (navigator.userAgent || navigator.vendor || '').toLowerCase();
    if (/android|iphone|ipad|ipod|iemobile|blackberry|opera mini|mobile/i.test(ua)) return true;
    if (ua.indexOf('macintosh') > -1 && navigator.maxTouchPoints > 1) return true;
    return false;
  }

  function unwrap(data) {
    // n8n "Respond to Webhook" often wraps the body in an array of items.
    if (Array.isArray(data)) return data[0] || {};
    return data || {};
  }

  function show(elm) { if (elm) elm.hidden = false; }
  function hide(elm) { if (elm) elm.hidden = true; }

  function safeJson(obj) {
    try { return JSON.stringify(obj, null, 2); } catch (_) { return String(obj); }
  }

  // Shallow-merge two plain objects into a new one (ES5-safe; no Object.assign).
  function merge(base, extra) {
    var out = {}, k;
    if (base) for (k in base) { if (base.hasOwnProperty(k)) out[k] = base[k]; }
    if (extra) for (k in extra) { if (extra.hasOwnProperty(k)) out[k] = extra[k]; }
    return out;
  }

  // Parse a numeric value out of a money-ish string (e.g. "$7.50", "5.00 /month").
  // Returns null when nothing numeric is present — GA4 `value` must be a Number.
  function toNumber(v) {
    if (v == null || v === '') return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }

  // Human-readable label for a clicked control — its trimmed visible text, or an
  // explicit data-track-label / aria-label override, collapsed + length-capped.
  function ctrlLabel(el) {
    if (!el) return null;
    var t = el.getAttribute && (el.getAttribute('data-track-label') || el.getAttribute('aria-label'));
    if (!t) t = el.textContent || '';
    t = t.replace(/\s+/g, ' ').trim();
    if (!t) return null;
    return t.length > 100 ? t.slice(0, 99) + '…' : t;
  }

  function Flow(root) {
    this.root = root;
    this.validateUrl = (root.dataset.validateUrl || '').trim();
    this.cancelUrl = (root.dataset.cancelUrl || '').trim();
    this.acceptUrl = (root.dataset.acceptUrl || '').trim();
    this.plansUrl = (root.dataset.plansUrl || '').trim(); // list-plans catalog (debug plan-id check only)
    this.tokenParam = root.dataset.tokenParam || 't';
    this.returnParam = root.dataset.returnParam || 'return';
    this.appDeepLink = (root.dataset.appDeepLink || 'pulsetto://home').trim(); // mobile: open the app
    this.homeUrl = (root.dataset.homeUrl || '/').trim();                       // desktop: website homepage
    this.startStep = root.dataset.startStep || 'intro';
    this.autoRedirectSeconds = parseInt(root.dataset.autoRedirectSeconds || '0', 10);
    this.designMode = !!(window.Shopify && window.Shopify.designMode);

    var params = new URLSearchParams(window.location.search);
    this.token = params.get(this.tokenParam);       // opaque — never inspected
    this.returnUrl = safeReturnUrl(params.get(this.returnParam));

    this.debugParam = root.dataset.debugParam || 'debug';
    var dbg = (params.get(this.debugParam) || '').toLowerCase();
    this.debug = root.dataset.debug === 'true' || dbg === '1' || dbg === 'true' || dbg === 'yes';

    this.subscription = null;
    this.customer = null;      // customer object from the validate response
    this.resultVariant = null; // which result variant is currently shown
    this.reason = null;        // value of the chosen cancel reason (answer)
    this.reasonLabel = null;   // human-readable label of that reason
    this.changePlanId = null;  // chosen selling plan id (change-plan / manage step)
    this.changeBusy = false;   // change-plan: one in-flight request at a time
    this.catalog = null;       // list-plans response (full Loop plan catalog) — debug only
    this.catalogById = null;   // catalog indexed by sellingPlanShopifyId — debug only
    this.catalogError = null;  // load failure message (HTTP/CORS/network) — debug only
    this.history = [];
    this.lastValidate = 'idle';

    // Collect step wrappers anywhere on the page.
    this.steps = {};
    var nodes = document.querySelectorAll('[data-cancel-step]');
    for (var i = 0; i < nodes.length; i++) {
      this.steps[nodes[i].getAttribute('data-cancel-step')] = nodes[i];
    }

    this.beginBtn = document.querySelector('[data-cancel-action="begin"]');
    this.stayBtn = document.querySelector('[data-cancel-stay]'); // intro "I'd like to stay" — shown only once the link validates
    this.checking = document.querySelector('[data-cancel-checking]');
    this.invalid = document.querySelector('[data-cancel-invalid]');
    this.errorNote = document.querySelector('[data-cancel-error]');

    this.applyDeviceLabels();
    this.bind();
    if (this.debug) this.initDebug();
    this.start();
  }

  // "Back to app" buttons carry both labels; on desktop swap to the homepage label.
  // Mobile keeps the server-rendered text, so it works even before JS runs.
  Flow.prototype.applyDeviceLabels = function () {
    if (isMobileDevice()) return;
    var btns = document.querySelectorAll('[data-cancel-action="return"][data-label-desktop]');
    for (var i = 0; i < btns.length; i++) {
      var desktop = btns[i].getAttribute('data-label-desktop');
      if (desktop) btns[i].textContent = desktop;
    }
  };

  Flow.prototype.bind = function () {
    var self = this;

    // Delegated click handling for every wired button on the page.
    document.addEventListener('click', function (e) {
      // Plain links the controller doesn't navigate (support mailto, testimonial
      // "read her full story") — track them, then let the browser follow the href.
      var link = e.target.closest && e.target.closest('a[href]');
      if (link && self.owns(link)) {
        if (link.classList.contains('scf-btn--contact') || (link.getAttribute('href') || '').indexOf('mailto:') === 0) {
          self.track('support_click', { tcf_label: ctrlLabel(link), tcf_status: link.getAttribute('href') });
        } else if (link.classList.contains('scf-testi__story')) {
          self.track('story_click', { tcf_label: ctrlLabel(link), tcf_status: link.getAttribute('href') });
        }
        // no preventDefault — navigation continues
      }

      var navBtn = e.target.closest && e.target.closest('[data-cancel-nav]');
      if (navBtn && self.owns(navBtn)) {
        e.preventDefault();
        if (navBtn.getAttribute('data-cancel-nav') === 'back') {
          self.track('back', { tcf_label: ctrlLabel(navBtn) });
          self.back();
        }
        return;
      }
      // Price-offer plan cards: select one + retarget the keep button. (Folded in
      // from a former per-section inline <script> so the offer section ships zero JS.)
      var priceCard = e.target.closest && e.target.closest('.scf-price-card');
      if (priceCard && self.owns(priceCard)) {
        self.selectPriceCard(priceCard);
        if (priceCard.classList.contains('scf-price-card--featured')) {
          self.track('offer_select', {
            tcf_label: priceCard.getAttribute('data-offer-label') || ctrlLabel(priceCard),
            tcf_offer_id: priceCard.getAttribute('data-accept-id'),
            tcf_offer_type: priceCard.getAttribute('data-accept-type'),
            tcf_value: toNumber(priceCard.getAttribute('data-amount')),
            tcf_currency: priceCard.getAttribute('data-currency')
          });
        }
        return;
      }
      // Change-plan (manage) radio cards — select one + enable the confirm button.
      var planCard = e.target.closest && e.target.closest('.scf-plan-card');
      if (planCard && self.owns(planCard)) {
        self.selectPlanCard(planCard);
        self.track('change_plan_select', {
          tcf_label: ctrlLabel(planCard),
          tcf_offer_id: planCard.getAttribute('data-loop-plan-id'),
          tcf_offer_type: 'swap'
        });
        return;
      }
      var actBtn = e.target.closest && e.target.closest('[data-cancel-action]');
      if (!actBtn || !self.owns(actBtn)) return;
      var action = actBtn.getAttribute('data-cancel-action');
      if (action === 'begin') {
        e.preventDefault();
        if (!actBtn.disabled) { self.track('begin', { tcf_label: ctrlLabel(actBtn) }); self.goto('reason'); }
      }
      else if (action === 'continue-reason') {
        e.preventDefault();
        self.track('reason_continue', { tcf_label: ctrlLabel(actBtn) });
        self.continueReason();
      }
      else if (action === 'keep') { e.preventDefault(); self.keep(actBtn); }
      else if (action === 'decline') {
        e.preventDefault();
        self.track('decline', { tcf_label: ctrlLabel(actBtn), tcf_status: actBtn.getAttribute('data-decline-target') || 'confirm' });
        self.decline(actBtn);
      }
      else if (action === 'confirm-cancel') { e.preventDefault(); self.confirmCancel(actBtn); }
      else if (action === 'change-plan') { e.preventDefault(); self.changePlan(actBtn); }
      else if (action === 'change-retry') {
        e.preventDefault();
        self.track('change_retry', { tcf_label: ctrlLabel(actBtn) });
        self.changeView(actBtn.closest('[data-cancel-step]'), 'select');
      }
      else if (action === 'retry') {
        e.preventDefault();
        self.track('retry', { tcf_label: ctrlLabel(actBtn) });
        self.start();
      }
      else if (action === 'return') {
        e.preventDefault();
        self.track('return', { tcf_label: ctrlLabel(actBtn), tcf_status: self.resolveReturnTarget() });
        self.returnToApp();
      }
    });

    // Record the chosen reason (answer) and enable the reason "continue" button.
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('input[name="subs-cancel-reason"]')) return;
      self.setReason(e.target);
      self.track('reason_select', {
        tcf_label: self.reasonLabel,
        tcf_status: e.target.getAttribute('data-offer-target') || null
      });
      var cont = self.steps.reason && self.steps.reason.querySelector('[data-cancel-action="continue-reason"]');
      if (cont) cont.disabled = false;
    });
  };

  // Only react to elements that belong to this flow (sibling step sections).
  Flow.prototype.owns = function (node) {
    return !!(node.closest('[data-cancel-step]'));
  };

  /* -------------------------------------------------------------------------
     GA4 / dataLayer analytics. Every meaningful interaction in the cancel flow
     is emitted through one funnel — track() — using a single, stable schema so
     GA4/GTM always read the SAME universal variables, whatever the event:

       event              tcf_<name>        (GTM trigger / GA4 event name)
       tcf_step           current step id   (intro|reason|offer-*|confirm|result)
       tcf_action         semantic action   (begin|reason_select|offer_accept|…)
       tcf_label          human-readable label (button text / chosen reason / …)
       tcf_reason         chosen cancel reason value      (null until picked)
       tcf_reason_label   chosen cancel reason label      (null until picked)
       tcf_offer_id       Loop deal / selling-plan id     (offer steps only)
       tcf_offer_type     discount|swap                   (offer steps only)
       tcf_value          numeric money value             (offer accepts)
       tcf_currency       currency symbol/code            (offer accepts)
       tcf_result_variant result screen shown             (result step only)
       tcf_status         outcome status                  (ok|error|invalid|…)

     Everything is namespaced with the tcf_ prefix ("the cancel flow") so it never
     collides with other dataLayer variables on the page. Events are pushed to BOTH
     window.dataLayer (the universal GTM bus, GTM-MMPDQSW) AND gtag() (direct to
     GA4 G-Z8QLMDN801), so they land whether or not a GTM tag is configured for
     them. NOTE: if you later build a GTM tag that forwards these tcf_ events to the
     SAME GA4 property, remove the gtag() block below to avoid double-counting.
  ------------------------------------------------------------------------- */
  Flow.prototype.track = function (name, extra) {
    extra = extra || {};
    // Never emit analytics from the Shopify theme editor — it would pollute GA4
    // with merchant click-throughs. (Live debug pages still track.)
    if (this.designMode) return;
    function pick(k) { return extra[k] != null && extra[k] !== '' ? extra[k] : null; }
    var payload = {
      tcf_step: this.current || this.startStep || null,
      tcf_action: pick('tcf_action') != null ? pick('tcf_action') : name,
      tcf_label: pick('tcf_label'),
      tcf_reason: this.reason || null,
      tcf_reason_label: this.reasonLabel || null,
      tcf_offer_id: pick('tcf_offer_id'),
      tcf_offer_type: pick('tcf_offer_type'),
      tcf_value: pick('tcf_value'),
      tcf_currency: pick('tcf_currency'),
      tcf_result_variant: pick('tcf_result_variant'),
      tcf_status: pick('tcf_status')
    };
    var eventName = 'tcf_' + name;
    // Universal bus — GTM reads the tcf_ variables off the dataLayer.
    try {
      window.dataLayer = window.dataLayer || [];
      var obj = { event: eventName };
      for (var k in payload) { if (payload.hasOwnProperty(k)) obj[k] = payload[k]; }
      window.dataLayer.push(obj);
    } catch (_) {}
    // Direct to GA4 — guarantees delivery even with no GTM tag wired.
    try { if (typeof window.gtag === 'function') window.gtag('event', eventName, payload); } catch (_) {}
    if (this.debug) this.debugLog('track · ' + eventName + (payload.tcf_label ? ' · ' + payload.tcf_label : ''));
  };

  Flow.prototype.showStep = function (id, push) {
    var target = this.steps[id];
    if (!target) return;
    // In the editor keep every step visible — but debug needs real switching,
    // so fall through to forceStep when debugging.
    if (this.designMode && !this.debug) return;
    if (push !== false && this.current && this.current !== id) this.history.push(this.current);
    Object.keys(this.steps).forEach(function (k) { this.steps[k].hidden = true; }, this);
    target.hidden = false;
    this.current = id;
    var heading = target.querySelector('.scf-h, h1, h2');
    // Universal step-view event: tcf_step is the step now shown, label is its heading.
    this.track('step_view', { tcf_label: heading ? ctrlLabel(heading) : id });
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: false }); } catch (_) { try { heading.focus(); } catch (__) {} }
    }
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch (_) {}
    if (this.debug) this.debugRefresh();
  };

  Flow.prototype.goto = function (id) {
    if (this.debug) this.debugLog((this.current || '∅') + ' → ' + id);
    this.showStep(id, true);
  };

  Flow.prototype.back = function () {
    var prev = this.history.pop();
    if (prev) { if (this.debug) this.debugLog((this.current || '∅') + ' ← ' + prev + ' (back)'); this.showStep(prev, false); }
  };

  Flow.prototype.post = function (url, body) {
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { status: res.status, ok: res.ok, data: unwrap(data) };
      });
    });
  };

  Flow.prototype.start = function () {
    var self = this;
    hide(this.invalid); hide(this.errorNote);
    this.track('flow_start', { tcf_status: this.token ? 'token' : 'no_token' });

    if (this.designMode && !this.debug) { // editor: no token — let merchants click through
      this.setBeginState('ready');
      return;
    }

    this.setBeginState('checking');
    this.lastValidate = 'pending';
    this.showStep(this.startStep, false);

    if (!this.token || !this.validateUrl) return this.fail('invalid');

    this.post(this.validateUrl, { token: this.token }).then(function (r) {
      if (r.status === 401 || r.status === 410) return self.fail('invalid');
      if (!r.ok || !r.data || !r.data.ok) return self.fail('invalid');
      self.subscription = r.data.subscription || {};
      self.customer = r.data.customer || {};
      self.validateData = r.data; // full unwrapped Loop response
      self.hydrate(self.subscription, self.customer);
      if (self.debug) {
        if (window.console) console.log('[subs-cancel] validate response (Loop):', r.data);
        self.debugLoop();
      }
      // Only an ACTIVE subscription can be cancelled. Anything already CANCELLED /
      // EXPIRED / PAUSED is treated as invalid so the user can't "cancel" twice.
      var status = (self.subscription.status || '').toString().toUpperCase();
      if (status && status !== 'ACTIVE') {
        if (self.debug) self.debugLog('validate → ' + status + ' (not cancellable)');
        return self.fail('invalid');
      }
      self.setBeginState('ready');
      self.lastValidate = 'ok';
      self.track('validate', { tcf_status: 'ok', tcf_label: status || 'ACTIVE' });
      self.renderChangePlans(); // populate the manage/change-plan step (no-op elsewhere)
      self.markCurrentPriceCards(); // disable the customer's current plan on the price offer (no-op elsewhere)
      if (self.debug) { self.debugLog('validate → ok'); self.debugRefresh(); self.debugPriceCards(); }
    }).catch(function (err) {
      if (window.console && console.error) console.error('[subs-cancel] validate failed:', err);
      self.fail('error');
    });
  };

  // Drive the step-1 begin button through its three states. The button itself is
  // the validation indicator: a disabled spinner while checking, the cancel-intent
  // label once an active subscription is confirmed, or a red disabled "link
  // expired" message on failure. Copy for each state comes from data-label-*.
  Flow.prototype.setBeginState = function (state) {
    var b = this.beginBtn;
    if (!b) return;
    var labelEl = b.querySelector('.scf-btn__label');
    var spinner = b.querySelector('.scf-spinner');
    function setText(t) { if (labelEl) labelEl.textContent = t; else b.textContent = t; }
    b.classList.remove('is-checking', 'is-ready', 'is-failed');
    // The "I'd like to stay" button is only meaningful when the link works.
    if (this.stayBtn) this.stayBtn.hidden = (state !== 'ready');
    if (state === 'checking') {
      b.classList.add('is-checking');
      b.disabled = true; b.setAttribute('aria-busy', 'true');
      if (spinner) spinner.hidden = false;
      setText(b.getAttribute('data-label-checking') || '');
    } else if (state === 'failed') {
      b.classList.add('is-failed');
      b.disabled = true; b.removeAttribute('aria-busy');
      if (spinner) spinner.hidden = true;
      setText(b.getAttribute('data-label-failed') || '');
    } else { // ready
      b.classList.add('is-ready');
      b.disabled = false; b.removeAttribute('aria-busy');
      if (spinner) spinner.hidden = true;
      setText(b.getAttribute('data-label-default') || '');
    }
  };

  // On failure the begin button shows the (red) failed state; a transient network
  // error also reveals the retry link, while an invalid/expired link does not.
  Flow.prototype.fail = function (kind) {
    this.setBeginState('failed');
    hide(this.invalid);
    if (kind === 'error') show(this.errorNote); else hide(this.errorNote);
    this.changeFail(); // surface the manage/change-plan failure view (no-op elsewhere)
    this.lastValidate = kind;
    this.track('validate', { tcf_status: kind }); // kind = 'invalid' (expired/used) | 'error' (network)
    if (this.debug) { this.debugLog('validate → ' + kind); this.debugRefresh(); }
  };

  Flow.prototype.hydrate = function (sub, cust) {
    sub = sub || {};
    cust = cust || {};
    // The Loop subscription may carry its plan/price either flat or nested in a
    // lines[] array (one entry per subscribed product) — read the first line as
    // a fallback so plan/price/title resolve in both shapes.
    var lines = Array.isArray(sub.lines) ? sub.lines : [];
    var line0 = lines[0] || {};
    // Subscription fields → [data-sub-field="plan|status|price|total|nextBilling|lineTitle|…"]
    var nodes = document.querySelectorAll('[data-sub-field]');
    for (var i = 0; i < nodes.length; i++) {
      var f = nodes[i].getAttribute('data-sub-field');
      var v = '';
      if (f === 'plan') v = sub.plan || line0.productTitle || '';
      else if (f === 'lineTitle') v = line0.productTitle || '';
      else if (f === 'price') v = fmtMoney(sub.price != null ? sub.price : (line0.price != null ? line0.price : sub.total), sub.currencyCode);
      else if (f === 'total') v = fmtMoney(sub.total, sub.currencyCode);
      else if (f === 'nextBilling') v = sub.nextBillingDate || '';
      else if (sub[f] != null) v = String(sub[f]);
      if (v) nodes[i].textContent = v;
    }
    // Customer fields → [data-customer-field="firstName|lastName|name|email|…"]
    var cnodes = document.querySelectorAll('[data-customer-field]');
    for (var j = 0; j < cnodes.length; j++) {
      var cf = cnodes[j].getAttribute('data-customer-field');
      var cv = '';
      if (cf === 'name') cv = cust.name || [cust.firstName, cust.lastName].filter(Boolean).join(' ');
      else if (cust[cf] != null) cv = String(cust[cf]);
      if (cv) cnodes[j].textContent = cv;
    }
    // Free-text result copy can carry an [email address] token (e.g. the cancelled
    // variant's "Confirmation email on its way to [email address]") — swap it for the
    // real address from the validate response. Token stays untouched if we have none.
    this.fillTextTokens({ '[email address]': cust.email });
  };

  // Replace literal tokens inside the result step's text nodes. Walks text nodes so
  // tokens work in any free-text setting (sync email line, body, checklist items).
  Flow.prototype.fillTextTokens = function (map) {
    var step = this.steps['result'];
    if (!step || !map) return;
    var tokens = [];
    for (var k in map) { if (map.hasOwnProperty(k) && map[k] != null && map[k] !== '') tokens.push(k); }
    if (!tokens.length) return;
    var walker = document.createTreeWalker(step, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      var t = node.nodeValue;
      if (!t) continue;
      for (var i = 0; i < tokens.length; i++) {
        if (t.indexOf(tokens[i]) !== -1) t = t.split(tokens[i]).join(String(map[tokens[i]]));
      }
      if (t !== node.nodeValue) node.nodeValue = t;
    }
  };

  // Capture the selected reason radio as the answer carried to the backend.
  Flow.prototype.setReason = function (input) {
    if (!input) return;
    this.reason = input.value || null;
    var label = input.closest && input.closest('label');
    var labelEl = label && (label.querySelector('.scf-reason__label') || label);
    this.reasonLabel = labelEl ? (labelEl.textContent || '').replace(/\s+/g, ' ').trim() : null;
  };

  // Index the validate response's plans by selling-plan id. Each plan may carry a
  // `current` flag marking the customer's existing plan. Shared by the price-offer
  // current-plan guard and the debug diagnostics.
  Flow.prototype.validatePlansById = function () {
    var data = this.validateData || {};
    var vplans = data.plans || (this.subscription && this.subscription.plans) || [];
    var vById = {};
    for (var i = 0; i < vplans.length; i++) {
      var vp = vplans[i] || {};
      var vid = vp.id != null ? vp.id : vp.sellingPlanShopifyId;
      if (vid != null && vid !== '') vById[String(vid)] = vp;
    }
    return vById;
  };

  // Price-offer: when the validate response flags a card's plan as the customer's
  // current plan (validateMatch.current === true), that plan can't be "kept" again —
  // disable the card, label it, and make sure the selection + keep button land on a
  // still-pickable plan. No-op when there's no offer-price step or no validate data.
  Flow.prototype.markCurrentPriceCards = function () {
    var step = this.steps['offer-price'];
    if (!step) return;
    var grid = step.querySelector('[data-price-grid]');
    if (!grid) return;
    var vById = this.validatePlansById();
    var label = grid.getAttribute('data-current-label') || 'Your current plan';
    var cards = grid.querySelectorAll('.scf-price-card');
    var firstSelectable = null;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var aid = (card.getAttribute('data-accept-id') || '').trim();
      var vp = aid ? vById[aid] : null;
      var existing = card.querySelector('.scf-price-card__current');
      if (vp && vp.current) {
        card.classList.add('scf-price-card--current');
        card.classList.remove('scf-price-card--featured');
        card.disabled = true;
        card.setAttribute('aria-disabled', 'true');
        card.setAttribute('aria-checked', 'false');
        if (!existing) {
          var tag = document.createElement('span');
          tag.className = 'scf-price-card__current';
          tag.textContent = label;
          card.appendChild(tag);
        }
      } else {
        card.classList.remove('scf-price-card--current');
        card.disabled = false;
        card.removeAttribute('aria-disabled');
        if (existing) existing.parentNode.removeChild(existing);
        if (!firstSelectable) firstSelectable = card;
      }
    }
    // If the highlighted card is the current plan (or nothing pickable is selected),
    // move the selection — and the keep button — onto the first selectable plan.
    var featured = grid.querySelector('.scf-price-card--featured:not(.scf-price-card--current)');
    if (!featured && firstSelectable) this.selectPriceCard(firstSelectable);
    var keepBtn = step.querySelector('[data-cancel-action="keep"]');
    if (keepBtn) keepBtn.disabled = !firstSelectable;
  };

  // Price-offer: highlight the chosen plan card and point the single "keep"
  // button at that card's result variant + accept id.
  Flow.prototype.selectPriceCard = function (card) {
    if (!card || card.disabled || card.classList.contains('scf-price-card--current')) return; // current plan isn't pickable
    var grid = card.closest('[data-price-grid]');
    if (!grid) return;
    var cards = grid.querySelectorAll('.scf-price-card');
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.remove('scf-price-card--featured');
      cards[i].setAttribute('aria-checked', 'false');
    }
    card.classList.add('scf-price-card--featured');
    card.setAttribute('aria-checked', 'true');
    var step = card.closest('[data-cancel-step]');
    var keepBtn = step && step.querySelector('[data-cancel-action="keep"]');
    if (!keepBtn) return;
    keepBtn.setAttribute('data-result-target', card.getAttribute('data-result-target') || 'welcome-back');
    var aid = card.getAttribute('data-accept-id');
    if (aid) keepBtn.setAttribute('data-accept-id', aid);
    else keepBtn.removeAttribute('data-accept-id');
    var atype = card.getAttribute('data-accept-type');
    if (atype) keepBtn.setAttribute('data-accept-type', atype);
    else keepBtn.removeAttribute('data-accept-type');
    var pid = card.getAttribute('data-product-id');
    if (pid) keepBtn.setAttribute('data-product-id', pid);
    else keepBtn.removeAttribute('data-product-id');
    // Carry the analytics value dimensions (amount / currency / label) so the
    // offer_accept event on the shared keep button reports the chosen plan's price.
    ['data-amount', 'data-currency', 'data-offer-label'].forEach(function (attr) {
      var v = card.getAttribute(attr);
      if (v) keepBtn.setAttribute(attr, v); else keepBtn.removeAttribute(attr);
    });
  };

  /* -------------------------------------------------------------------------
     Change plan (manage step). Additive: reuses the opaque token, the post()
     fetch wrapper, and busy() — leaves validate/cancel untouched.
  ------------------------------------------------------------------------- */

  // Highlight the chosen plan radio and enable the confirm button.
  Flow.prototype.selectPlanCard = function (card) {
    var grid = card.closest('[data-plan-grid]');
    if (!grid) return;
    var cards = grid.querySelectorAll('.scf-plan-card');
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.remove('scf-plan-card--selected');
      cards[i].setAttribute('aria-checked', 'false');
    }
    card.classList.add('scf-plan-card--selected');
    card.setAttribute('aria-checked', 'true');
    this.changePlanId = card.getAttribute('data-loop-plan-id');
    var step = card.closest('[data-cancel-step]');
    var confirmBtn = step && step.querySelector('[data-cancel-action="change-plan"]');
    if (confirmBtn) confirmBtn.disabled = false;
  };

  // Render the plan radios once validate resolves. The merchant curates WHICH
  // plans to offer via [data-plan-config] markers (id + optional overrides); the
  // live plan data is matched to those ids from the validate response:
  //   data.plans[] = [ { id|sellingPlanShopifyId, label|name, description?, price?,
  //                      currencyCode?, current? }, ... ]
  // Only curated ids the customer is eligible for are shown (current plan skipped),
  // in the merchant's order. With no curation blocks, every returned plan is shown.
  // No-op when there is no change-plan step on the page.
  Flow.prototype.renderChangePlans = function () {
    var step = this.steps['change-plan'];
    if (!step) return;
    var grid = step.querySelector('[data-plan-grid]');
    if (!grid) return;

    // Index the validate plans by id (accept either `id` or `sellingPlanShopifyId`).
    var data = this.validateData || {};
    var plans = data.plans || (this.subscription && this.subscription.plans) || [];
    var byId = {};
    for (var i = 0; i < plans.length; i++) {
      var pp = plans[i] || {};
      var pid = pp.id != null ? pp.id : pp.sellingPlanShopifyId;
      if (pid != null && pid !== '') byId[String(pid)] = pp;
    }

    // Build the render list: curated blocks (allowlist + order + overrides) matched
    // by id; or, with no blocks, every eligible plan the webhook returned.
    var list = [];
    var configs = step.querySelectorAll('[data-plan-config]');
    if (configs.length) {
      for (var c = 0; c < configs.length; c++) {
        var cfg = configs[c];
        var cid = (cfg.getAttribute('data-loop-plan-id') || '').trim();
        if (!cid) continue;
        var match = byId[cid];
        if (!match || match.current) continue; // skip ineligible / current plan
        list.push({ id: cid, plan: match, cfg: cfg });
      }
    } else {
      for (var key in byId) {
        if (byId.hasOwnProperty(key) && !byId[key].current) list.push({ id: key, plan: byId[key], cfg: null });
      }
    }

    grid.innerHTML = ''; // wipe editor placeholders / any prior render
    for (var j = 0; j < list.length; j++) this.appendPlanCard(grid, list[j]);
    this.changeView(step, grid.children.length ? 'select' : 'empty');
    if (this.debug) this.debugPlanConfigs();
  };

  // Debug-only: GET the list-plans catalog (controller's "List-plans webhook URL")
  // so each Change-plan block's selling-plan id can be checked against the FULL Loop
  // catalog — independent of any customer/token, so it works in the theme editor too.
  // Read-only GET, no token, no body. Caches the result; re-renders the debug boxes
  // when it lands. No-op (and never fetched) outside debug mode.
  Flow.prototype.loadCatalog = function (done) {
    var self = this;
    if (!this.debug || !this.plansUrl || this.catalogById) { if (done) done(); return; }
    if (this.catalogLoading) { if (done) this.catalogWaiters = (this.catalogWaiters || []).concat(done); return; }
    this.catalogLoading = true;
    this.catalogWaiters = done ? [done] : [];
    var finish = function () {
      self.catalogLoading = false;
      var ws = self.catalogWaiters || []; self.catalogWaiters = [];
      for (var w = 0; w < ws.length; w++) { try { ws[w](); } catch (_) {} }
    };
    fetch(this.plansUrl, { method: 'GET', mode: 'cors', headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (d) {
          return { ok: res.ok, status: res.status, data: unwrap(d) };
        });
      })
      .then(function (r) {
        var data = r.data || {};
        var plans = Array.isArray(data.plans) ? data.plans : [];
        var byId = {};
        for (var i = 0; i < plans.length; i++) {
          var p = plans[i] || {};
          var id = p.sellingPlanShopifyId != null ? p.sellingPlanShopifyId : p.id;
          if (id != null && id !== '') byId[String(id)] = p;
        }
        self.catalog = data;
        self.catalogById = byId;
        self.catalogError = r.ok ? null : ('HTTP ' + r.status);
        self.debugLog('catalog → ' + (r.ok ? ('ok · ' + plans.length + ' plans') : ('fail ' + r.status)));
        finish();
      })
      .catch(function (err) {
        self.catalogById = {};
        self.catalogError = (err && err.message) || 'network/CORS error';
        self.debugLog('catalog → error: ' + self.catalogError);
        finish();
      });
  };

  // Debug-only: render a visible box per curated plan block showing whether its
  // selling-plan id is (a) a REAL plan in the list-plans catalog, and (b) returned
  // for THIS customer by the validate webhook. (a) needs only the controller's
  // List-plans URL — so it validates ids in the theme editor with no token; (b) adds
  // live eligibility once validate resolves. Appended to the change card region (a
  // sibling of the view divs, so it stays visible whichever view is active).
  Flow.prototype.debugPlanConfigs = function () {
    var step = this.steps['change-plan'];
    if (!step) return;

    // Kick off the catalog load once; re-render when it arrives.
    if (this.plansUrl && !this.catalogById) { var self = this; this.loadCatalog(function () { self.debugPlanConfigs(); }); }

    // Validate plans (this customer's eligibility), indexed by id.
    var data = this.validateData || {};
    var vplans = data.plans || (this.subscription && this.subscription.plans) || [];
    var vById = {};
    for (var i = 0; i < vplans.length; i++) {
      var vp = vplans[i] || {};
      var vid = vp.id != null ? vp.id : vp.sellingPlanShopifyId;
      if (vid != null && vid !== '') vById[String(vid)] = vp;
    }
    var catById = this.catalogById; // null until loaded; {} once loaded (maybe empty)

    var region = step.querySelector('[data-change-region]') || step;
    var prior = region.querySelector('[data-plan-debug-list]');
    if (prior) prior.parentNode.removeChild(prior); // re-run safe

    var wrap = document.createElement('div');
    wrap.className = 'scf-plan-debug-list';
    wrap.setAttribute('data-plan-debug-list', '');
    wrap.setAttribute('aria-hidden', 'true'); // keep out of the card's aria-live

    var catLine = !this.plansUrl ? 'catalog: no List-plans URL set on the controller'
      : this.catalogError ? ('catalog: load failed — ' + this.catalogError)
      : catById ? ('catalog: ' + Object.keys(catById).length + ' plans loaded')
      : 'catalog: loading…';
    var head = document.createElement('div');
    head.className = 'scf-plan-debug__head';
    head.textContent = '🔎 Plan id check · ' + catLine + ' · validate returned ' + Object.keys(vById).length + ' plan(s)';
    wrap.appendChild(head);

    var configs = step.querySelectorAll('[data-plan-config]');
    if (!configs.length) {
      var none = document.createElement('div');
      none.className = 'scf-plan-debug scf-plan-debug--warn';
      none.textContent = 'No plan blocks configured — every plan validate returns is shown uncurated.';
      wrap.appendChild(none);
    }

    for (var c = 0; c < configs.length; c++) {
      var cfg = configs[c];
      var id = (cfg.getAttribute('data-loop-plan-id') || '').trim();
      var inCat = id && catById ? catById[id] : null;
      var vMatch = id ? vById[id] : null;

      var box = document.createElement('div');
      box.className = 'scf-plan-debug';

      var title = document.createElement('div');
      title.className = 'scf-plan-debug__title';
      title.appendChild(document.createTextNode('Block ' + (c + 1) + ' · id ' + (id || '(empty)') + '  '));

      // Pill 1 — does the id exist in the Loop catalog at all?
      var p1 = document.createElement('span');
      p1.className = 'scf-plan-debug__pill';
      if (!id) { p1.textContent = 'no id set'; p1.classList.add('scf-plan-debug__pill--bad'); }
      else if (!this.plansUrl) { p1.textContent = 'catalog n/a'; p1.classList.add('scf-plan-debug__pill--na'); }
      else if (!catById) { p1.textContent = 'catalog loading…'; p1.classList.add('scf-plan-debug__pill--na'); }
      else if (this.catalogError) { p1.textContent = 'catalog error'; p1.classList.add('scf-plan-debug__pill--na'); }
      else if (inCat) { p1.textContent = 'in catalog ✓'; p1.classList.add('scf-plan-debug__pill--ok'); }
      else { p1.textContent = 'not in catalog ✗'; p1.classList.add('scf-plan-debug__pill--bad'); }
      title.appendChild(p1);
      title.appendChild(document.createTextNode(' '));

      // Pill 2 — did validate return it for this customer?
      var p2 = document.createElement('span');
      p2.className = 'scf-plan-debug__pill';
      if (!Object.keys(vById).length) { p2.textContent = 'validate n/a'; p2.classList.add('scf-plan-debug__pill--na'); }
      else if (!vMatch) { p2.textContent = 'validate: not returned'; p2.classList.add('scf-plan-debug__pill--bad'); }
      else if (vMatch.current) { p2.textContent = 'validate: current (skipped)'; p2.classList.add('scf-plan-debug__pill--warn'); }
      else { p2.textContent = 'validate: shown ✓'; p2.classList.add('scf-plan-debug__pill--ok'); }
      title.appendChild(p2);
      box.appendChild(title);

      var pre = document.createElement('pre');
      pre.className = 'scf-plan-debug__data';
      var dump;
      if (!id) {
        dump = 'data-loop-plan-id is empty — pick a plan in this block\'s dropdown.';
      } else {
        var parts = [];
        if (this.plansUrl && catById) parts.push('// catalog (list-plans)\n' + (inCat ? safeJson(inCat) : 'No plan in the catalog has id ' + id + '.'));
        if (Object.keys(vById).length) parts.push('// validate (this customer)\n' + (vMatch ? safeJson(vMatch) : 'Not returned by validate for this customer.'));
        if (!parts.length) parts.push('No catalog and no validate data yet — set the List-plans URL or open with a valid token.');
        dump = parts.join('\n\n');
      }
      pre.textContent = dump;
      box.appendChild(pre);

      // Echo any merchant overrides set on the block (these win over live data).
      var ov = [];
      ['data-label', 'data-amount', 'data-description'].forEach(function (a) {
        var v = cfg.getAttribute(a);
        if (v) ov.push(a.replace('data-', '') + ' = ' + v);
      });
      if (ov.length) {
        var o = document.createElement('div');
        o.className = 'scf-plan-debug__ov';
        o.textContent = 'overrides: ' + ov.join('  ·  ');
        box.appendChild(o);
      }

      wrap.appendChild(box);
    }

    region.appendChild(wrap);
  };

  // Debug-only: render a JSON <pre> after each price-offer card inside
  // [data-price-grid], dumping that card's wired config (accept id / type / result
  // target). For "swap" cards whose accept id is a selling-plan id, it also resolves
  // that id against the list-plans catalog and the validate plans[] (same checks as
  // the change-plan boxes). Each box spans the full grid width so the 2-col layout
  // is preserved. No-op when there's no offer-price step.
  Flow.prototype.debugPriceCards = function () {
    if (!this.debug) return; // debug-only diagnostics — never render on the live page
    var step = this.steps['offer-price'];
    if (!step) return;
    var grid = step.querySelector('[data-price-grid]');
    if (!grid) return;

    // Kick off the catalog load once; re-render when it arrives.
    if (this.plansUrl && !this.catalogById) { var self = this; this.loadCatalog(function () { self.debugPriceCards(); }); }

    var data = this.validateData || {};
    var vplans = data.plans || (this.subscription && this.subscription.plans) || [];
    var vById = {};
    for (var i = 0; i < vplans.length; i++) {
      var vp = vplans[i] || {};
      var vid = vp.id != null ? vp.id : vp.sellingPlanShopifyId;
      if (vid != null && vid !== '') vById[String(vid)] = vp;
    }
    var catById = this.catalogById;

    // Clear any prior debug boxes (re-run safe).
    var olds = grid.querySelectorAll('[data-price-debug]');
    for (var o = 0; o < olds.length; o++) olds[o].parentNode.removeChild(olds[o]);

    var cards = grid.querySelectorAll('.scf-price-card');
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      var acceptId = (card.getAttribute('data-accept-id') || '').trim();
      var acceptType = (card.getAttribute('data-accept-type') || '').trim();
      var resultTarget = (card.getAttribute('data-result-target') || '').trim();
      var productId = (card.getAttribute('data-product-id') || '').trim();

      var info = {
        acceptId: acceptId || null,
        acceptType: acceptType || null,
        resultTarget: resultTarget || null,
        productId: productId || null
      };
      // Selling-plan resolution only makes sense for swaps (discount ids aren't plans).
      if (acceptType === 'swap' && acceptId) {
        info.catalogMatch = catById ? (catById[acceptId] || null) : 'catalog not loaded';
        info.validateMatch = Object.keys(vById).length ? (vById[acceptId] || null) : 'validate n/a';
      }

      var box = document.createElement('div');
      box.className = 'scf-plan-debug scf-plan-debug--price';
      box.setAttribute('data-price-debug', '');
      box.setAttribute('aria-hidden', 'true');

      var title = document.createElement('div');
      title.className = 'scf-plan-debug__title';
      title.appendChild(document.createTextNode('Card ' + (c + 1) + '  '));
      var pill = document.createElement('span');
      pill.className = 'scf-plan-debug__pill';
      if (acceptType === 'swap' && acceptId) {
        if (!catById) { pill.textContent = 'catalog loading…'; pill.classList.add('scf-plan-debug__pill--na'); }
        else if (this.catalogError) { pill.textContent = 'catalog error'; pill.classList.add('scf-plan-debug__pill--na'); }
        else if (catById[acceptId]) { pill.textContent = 'swap · in catalog ✓'; pill.classList.add('scf-plan-debug__pill--ok'); }
        else { pill.textContent = 'swap · not in catalog ✗'; pill.classList.add('scf-plan-debug__pill--bad'); }
      } else if (acceptType === 'discount') {
        pill.textContent = 'discount (not a selling plan)'; pill.classList.add('scf-plan-debug__pill--na');
      } else if (!acceptId) {
        pill.textContent = 'no accept id'; pill.classList.add('scf-plan-debug__pill--warn');
      } else {
        pill.textContent = acceptType || 'no type'; pill.classList.add('scf-plan-debug__pill--na');
      }
      title.appendChild(pill);
      box.appendChild(title);

      var pre = document.createElement('pre');
      pre.className = 'scf-plan-debug__data';
      pre.textContent = safeJson(info);
      box.appendChild(pre);

      // Insert the box right after its card; full-width CSS drops it onto its own row.
      if (card.nextSibling) grid.insertBefore(box, card.nextSibling);
      else grid.appendChild(box);
    }
  };

  // Build one plan radio. Live data comes from the matched validate plan; the
  // merchant's [data-plan-config] overrides (label / amount / description) win when
  // set. All text is set via textContent — webhook + setting strings are never HTML.
  Flow.prototype.appendPlanCard = function (grid, item) {
    var p = item.plan || {};
    var cfg = item.cfg;
    function ov(attr) { return cfg ? (cfg.getAttribute(attr) || '').trim() : ''; }

    var card = document.createElement('button');
    card.type = 'button';
    card.className = 'scf-plan-card';
    card.setAttribute('role', 'radio');
    card.setAttribute('aria-checked', 'false');
    card.setAttribute('data-loop-plan-id', item.id);

    var radio = document.createElement('span');
    radio.className = 'scf-plan-card__radio';
    radio.setAttribute('aria-hidden', 'true');

    var text = document.createElement('span');
    text.className = 'scf-plan-card__text';
    var label = document.createElement('span');
    label.className = 'scf-plan-card__label';
    label.textContent = ov('data-label') || p.label || p.name || p.planName || ('Plan ' + item.id);
    text.appendChild(label);

    var parts = [];
    var desc = ov('data-description') || p.description;
    if (desc) parts.push(desc);
    var amount = ov('data-amount');
    if (!amount && p.price != null && p.price !== '') amount = fmtMoney(p.price, p.currencyCode);
    if (amount) parts.push(amount);
    if (parts.length) {
      var d = document.createElement('span');
      d.className = 'scf-plan-card__desc';
      d.textContent = parts.join(' · ');
      text.appendChild(d);
    }

    card.appendChild(radio);
    card.appendChild(text);
    grid.appendChild(card);
  };

  // Validate failed (invalid/expired/network) — surface the change-plan step's
  // failure view so an expired manage link doesn't hang on the loading spinner.
  // No-op when there is no change-plan step on the page.
  Flow.prototype.changeFail = function () {
    var step = this.steps['change-plan'];
    if (step) this.changeView(step, 'failure');
  };

  // Toggle the change-plan card between its select / success / failure views and
  // move focus to the shown view's heading (the card's aria-live announces it).
  Flow.prototype.changeView = function (step, name) {
    if (!step) return;
    var views = step.querySelectorAll('[data-change-view]');
    var shown = null;
    for (var i = 0; i < views.length; i++) {
      var on = views[i].getAttribute('data-change-view') === name;
      views[i].hidden = !on;
      if (on) shown = views[i];
    }
    if (shown) {
      var h = shown.querySelector('.scf-change__title') || shown.querySelector('.scf-h');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (_) {} }
    }
  };

  // Fill the success view's summary rows from Loop's `change` (the real applied
  // plan). Reuses the same field/format approach as hydrate(); hides the summary
  // when Loop returns nothing to show.
  Flow.prototype.hydrateChange = function (change, step) {
    change = change || {};
    var nodes = step.querySelectorAll('[data-change-field]');
    var any = false;
    for (var i = 0; i < nodes.length; i++) {
      var f = nodes[i].getAttribute('data-change-field');
      var v = '';
      if (f === 'price') v = fmtMoney(change.price != null ? change.price : change.total, change.currencyCode);
      else if (f === 'nextBilling') v = change.nextBillingDate || '';
      else if (f === 'plan') v = change.plan || '';
      else if (change[f] != null) v = String(change[f]);
      if (v) { nodes[i].textContent = v; any = true; }
    }
    var summary = step.querySelector('[data-change-summary]');
    if (summary) summary.hidden = !any;
  };

  // Submit the chosen plan as a Loop swap. One in-flight request only; POSTs the
  // Loop selling-plan id to the (reused) offer-accept webhook as `offer` (a Number)
  // with offerType "swap" — identical to the proven retention-swap path. Branches
  // on response.ok / status only — never assumes a body shape on failure. The
  // token is sent in the body verbatim and never written to the DOM or logged.
  Flow.prototype.changePlan = function (btn) {
    var self = this;
    if (this.changeBusy) return;                      // prevent double-submit
    var step = btn.closest('[data-cancel-step]');
    var id = parseInt(this.changePlanId, 10);         // Loop selling-plan id as a Number
    var meta = { tcf_label: ctrlLabel(btn), tcf_offer_id: this.changePlanId, tcf_offer_type: 'swap' };
    if (!step || !this.acceptUrl || !isFinite(id)) {
      this.track('change_plan_error', merge(meta, { tcf_status: 'no_plan_selected' }));
      this.changeView(step, 'failure'); return;
    }
    this.track('change_plan', meta);                   // user submitted a plan swap
    this.changeBusy = true;
    this.busy(btn, true);
    this.post(this.acceptUrl, { token: this.token, offer: id, offerType: 'swap' }).then(function (r) {
      self.changeBusy = false;
      self.busy(btn, false);
      if (r.ok && r.data && r.data.ok) {
        self.change = r.data.change || null;          // real applied Loop plan
        self.acceptData = r.data;                     // full unwrapped Loop response
        if (self.debug) {
          if (window.console) console.log('[subs-cancel] change-plan response (Loop):', r.data);
          self.debugLog('change-plan → ok (swap ' + id + ')');
          self.debugLoop();
        }
        self.hydrateChange(self.change, step);
        self.track('change_plan_success', meta);
        self.changeView(step, 'success');
      } else {
        if (self.debug) self.debugLog('change-plan → fail (status ' + r.status + ')');
        self.track('change_plan_error', merge(meta, { tcf_status: 'http_' + r.status }));
        self.changeView(step, 'failure');           // 401 / non-200 → failure + retry
      }
    }).catch(function (err) {
      self.changeBusy = false;
      self.busy(btn, false);
      if (window.console && console.error) console.error('[subs-cancel] change-plan failed:', err);
      self.track('change_plan_error', merge(meta, { tcf_status: 'network_error' }));
      self.changeView(step, 'failure');
    });
  };

  // "Cancel anyway" on an offer step. Routes to the button's data-decline-target
  // (e.g. the retention "testimonial" interstitial) when that step exists on the
  // page, otherwise straight to the final confirm step. Keeps the controller
  // backwards-compatible: offers with no target — and the interstitial's own
  // "Cancel anyway" link — fall through to confirm.
  Flow.prototype.decline = function (btn) {
    var target = (btn && btn.getAttribute('data-decline-target')) || 'confirm';
    if (!this.steps[target]) target = 'confirm';
    this.goto(target);
  };

  Flow.prototype.continueReason = function () {
    var chosen = this.steps.reason && this.steps.reason.querySelector('input[name="subs-cancel-reason"]:checked');
    if (!chosen) return;
    this.setReason(chosen); // covers a pre-checked radio that never fired "change"
    var target = chosen.getAttribute('data-offer-target') || 'confirm';
    this.goto(target);
  };

  // "Keep my subscription" / accept a retention offer. When the chosen offer
  // carries a Loop id (+type), this awaits the plan-update automation and only
  // advances to the result on success; on failure the user stays on the step.
  Flow.prototype.keep = function (btn) {
    var self = this;
    var variant = btn.getAttribute('data-result-target') || 'welcome-back';
    var offerId = btn.getAttribute('data-accept-id');
    var offerType = btn.getAttribute('data-accept-type') || null; // 'discount' | 'swap'
    var productId = btn.getAttribute('data-product-id') || null;  // Shopify product id for Loop to match
    // Shared analytics dimensions for this keep/accept (value comes from the
    // selected price card's amount, copied onto the keep button by selectPriceCard).
    var meta = {
      tcf_label: btn.getAttribute('data-offer-label') || ctrlLabel(btn),
      tcf_offer_id: offerId,
      tcf_offer_type: offerType,
      tcf_value: toNumber(btn.getAttribute('data-amount')),
      tcf_currency: btn.getAttribute('data-currency'),
      tcf_result_variant: variant
    };

    if (this.acceptUrl && offerId) {
      this.track('offer_accept', meta); // user clicked to accept a real retention offer
      this.keepError(btn, false);
      this.busy(btn, true);
      this.post(this.acceptUrl, {
        token: this.token, offer: offerId, offerType: offerType,
        productId: productId,
        reason: this.reason, reasonLabel: this.reasonLabel
      }).then(function (r) {
        self.busy(btn, false);
        if (r.ok && r.data && r.data.ok) {
          self.change = r.data.change || null; // real applied plan, for result hydration
          self.acceptData = r.data;            // full unwrapped Loop response
          if (self.debug) {
            if (window.console) console.log('[subs-cancel] accept response (Loop):', r.data);
            self.debugLog('accept → ok (' + (offerType || 'offer') + ': ' + offerId + ')');
            self.debugLoop();
          }
          self.track('offer_accept_success', meta); // retention WON — offer applied server-side
          self.showResult(variant);
        } else {
          self.track('offer_accept_error', merge(meta,{ tcf_status: 'plan_update_failed' }));
          self.keepError(btn, true); // plan update failed → stay on the offer to retry
        }
      }).catch(function (err) {
        self.busy(btn, false);
        if (window.console && console.error) console.error('[subs-cancel] accept failed:', err);
        self.track('offer_accept_error', merge(meta,{ tcf_status: 'network_error' }));
        self.keepError(btn, true);
      });
      return;
    }
    // No Loop change attached — just "kept" (testimonial / patience / time / support).
    this.track('offer_keep', meta);
    this.showResult(variant);
  };

  // Toggle the offer step's plan-update error note.
  Flow.prototype.keepError = function (btn, on) {
    var step = btn.closest && btn.closest('[data-cancel-step]');
    var note = step && step.querySelector('[data-cancel-error]');
    if (note) note.hidden = !on;
  };

  Flow.prototype.confirmCancel = function (btn) {
    var self = this;
    var meta = { tcf_label: ctrlLabel(btn) }; // capture before busy() wipes the label
    this.track('confirm_cancel', meta);        // user confirmed the final cancel
    this.busy(btn, true);
    this.post(this.cancelUrl, { token: this.token, reason: this.reason, reasonLabel: this.reasonLabel }).then(function (r) {
      self.busy(btn, false);
      if (r.ok && r.data && r.data.ok) {
        self.cancelData = r.data; // full unwrapped Loop response
        if (self.debug) {
          if (window.console) console.log('[subs-cancel] cancel response (Loop):', r.data);
          self.debugLog('cancel → ok'); self.debugLoop();
        }
        self.track('cancel_success', merge(meta, { tcf_result_variant: 'cancelled', tcf_status: 'ok' })); // churn confirmed
        return self.showResult('cancelled');
      }
      self.track('cancel_error', merge(meta, { tcf_status: 'http_' + r.status }));
      self.cancelError(btn);
    }).catch(function (err) {
      self.busy(btn, false);
      if (window.console && console.error) console.error('[subs-cancel] cancel failed:', err);
      self.track('cancel_error', merge(meta, { tcf_status: 'network_error' }));
      self.cancelError(btn);
    });
  };

  Flow.prototype.cancelError = function (btn) {
    var note = this.steps.confirm && this.steps.confirm.querySelector('[data-cancel-error]');
    if (note) show(note);
  };

  Flow.prototype.setResultVariant = function (variant) {
    var step = this.steps.result;
    if (!step) return;
    var variants = step.querySelectorAll('[data-result-variant]');
    var matched = false;
    for (var i = 0; i < variants.length; i++) {
      var on = variants[i].getAttribute('data-result-variant') === variant;
      variants[i].hidden = !on;
      if (on) matched = true;
    }
    // Fall back to the first variant if the requested one isn't authored.
    if (!matched && variants.length) variants[0].hidden = false;
    this.resultVariant = matched ? variant
      : (variants.length ? variants[0].getAttribute('data-result-variant') : null);
    // Fill result-copy tokens on every display path. [date] → the applied plan's
    // next billing date from the accept response (change.nextBillingDate), falling
    // back to the subscription's date when no plan change was made (plain keep →
    // welcome-back). [email address] → customer email.
    var nextBilling = (this.change && this.change.nextBillingDate) ||
                      (this.subscription && this.subscription.nextBillingDate) || '';
    this.fillTextTokens({
      '[date]': nextBilling,
      '[email address]': this.customer && this.customer.email
    });
    if (this.debug) this.debugRefresh();
  };

  Flow.prototype.showResult = function (variant) {
    this.setResultVariant(variant); // fills [date] / [email address] tokens for the shown variant
    if (this.debug) this.debugLog('result: ' + variant);
    this.goto('result');
    // Terminal screen reached — report which outcome the user landed on.
    this.track('result_view', { tcf_result_variant: this.resultVariant || variant, tcf_label: this.resultVariant || variant });
    if (variant === 'cancelled' && this.autoRedirectSeconds > 0) {
      var self = this;
      setTimeout(function () { window.location.href = self.resolveReturnTarget(); }, this.autoRedirectSeconds * 1000);
    }
  };

  // Where "Back to app" sends the user. An explicit ?return= override always
  // wins; otherwise open the app on mobile and fall back to the homepage on desktop.
  Flow.prototype.resolveReturnTarget = function () {
    if (this.returnUrl) return this.returnUrl;
    if (isMobileDevice()) return this.appDeepLink;
    return this.homeUrl;
  };

  Flow.prototype.returnToApp = function () {
    window.location.href = this.resolveReturnTarget();
  };

  Flow.prototype.busy = function (btn, on) {
    if (!btn) return;
    if (on) {
      btn.disabled = true;
      btn.dataset.label = btn.textContent;
      btn.textContent = '';
      var sp = document.createElement('span');
      sp.className = 'scf-spinner';
      sp.setAttribute('aria-hidden', 'true');
      btn.appendChild(sp);
    } else {
      btn.disabled = false;
      if (btn.dataset.label != null) { btn.textContent = btn.dataset.label; delete btn.dataset.label; }
    }
  };

  /* -------------------------------------------------------------------------
     Debug mode — enabled by the controller setting or ?debug=1. Adds a floating
     panel to jump to any step, preview where each action leads, mock the
     validate result, and watch a live flow log. Never loads unless debug is on.
  ------------------------------------------------------------------------- */

  function dbgText(el) {
    var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.length > 32 ? t.slice(0, 31) + '…' : (t || '(button)');
  }

  // Resolve where a wired control would send the user.
  function dbgDescribe(self, el) {
    if (el.getAttribute('data-cancel-nav') === 'back') return { label: dbgText(el), to: 'back ↩' };
    var a = el.getAttribute('data-cancel-action');
    var to;
    switch (a) {
      case 'begin': to = el.disabled ? 'reason (locked until valid)' : 'reason'; break;
      case 'continue-reason': to = 'selected reason → its offer'; break;
      case 'keep': to = 'result: ' + (el.getAttribute('data-result-target') || 'welcome-back'); break;
      case 'decline': var dt = el.getAttribute('data-decline-target'); to = (dt && self.steps[dt]) ? dt : 'confirm'; break;
      case 'confirm-cancel': to = 'POST cancel → result: cancelled'; break;
      case 'change-plan': to = el.disabled ? 'POST change-plan (select a plan first)' : 'POST change-plan → success/failure'; break;
      case 'change-retry': to = 'change-plan: back to select'; break;
      case 'retry': to = 're-run validate'; break;
      case 'return': to = '↗ ' + self.resolveReturnTarget(); break;
      default: to = '?';
    }
    return { label: dbgText(el), to: to };
  }

  // Jump to a step regardless of design/live mode (debug-only navigation).
  Flow.prototype.forceStep = function (id) {
    var t = this.steps[id];
    if (!t) return;
    Object.keys(this.steps).forEach(function (k) { this.steps[k].hidden = true; }, this);
    t.hidden = false;
    if (this.current && this.current !== id) this.history.push(this.current);
    this.current = id;
    try { window.scrollTo({ top: 0 }); } catch (_) {}
    this.debugLog('jump → ' + id);
    this.debugRefresh();
  };

  Flow.prototype.mockValid = function () {
    hide(this.invalid); hide(this.errorNote); hide(this.checking);
    this.subscription = { plan: 'Premium (mock)', status: 'ACTIVE', price: 12, currencyCode: 'USD', nextBillingDate: 'June 28, 2026' };
    this.customer = { firstName: 'Alex', lastName: 'Doe', name: 'Alex Doe', email: 'alex@example.com' };
    this.validateData = { ok: true, subscription: this.subscription, customer: this.customer };
    this.hydrate(this.subscription, this.customer);
    if (this.beginBtn) this.beginBtn.disabled = false;
    this.lastValidate = 'ok';
    this.debugLog('mock validate → ok');
    this.debugLoop();
    this.debugRefresh();
  };

  Flow.prototype.debugLog = function (msg) {
    if (window.console) console.log('[subs-cancel] ' + msg);
    if (this.dbg && this.dbg.log) {
      var line = document.createElement('div');
      line.className = 'scf-debug__logline';
      line.textContent = msg;
      this.dbg.log.insertBefore(line, this.dbg.log.firstChild);
    }
  };

  // Dump every field pulled from the Loop API (validate / accept / cancel
  // responses) into the debug panel so nothing is hidden behind [data-sub-field].
  Flow.prototype.debugLoop = function () {
    if (!this.dbg || !this.dbg.loop) return;
    function block(title, obj) {
      var body;
      try { body = obj == null ? '—' : JSON.stringify(obj, null, 2); }
      catch (_) { body = String(obj); }
      return '// ' + title + '\n' + body;
    }
    this.dbg.loop.textContent = [
      block('validate → customer', this.customer),
      block('validate → subscription', this.subscription),
      block('validate → full response', this.validateData),
      block('accept → change', this.change),
      block('accept → full response', this.acceptData),
      block('cancel → full response', this.cancelData)
    ].join('\n\n');
  };

  Flow.prototype.debugRefresh = function () {
    if (!this.dbg) return;
    this.dbg.cur.textContent = this.current || '—';
    this.dbg.pill.textContent = this.lastValidate;
    this.dbg.pill.className = 'scf-debug__pill scf-debug__pill--' +
      (this.lastValidate === 'ok' ? 'ok' : (this.lastValidate === 'pending' ? 'wait' : 'bad'));

    var host = this.dbg.next;
    host.innerHTML = '';
    var cur = this.steps[this.current];
    if (cur) {
      if (this.current === 'reason') {
        var radios = cur.querySelectorAll('input[name="subs-cancel-reason"]');
        for (var r = 0; r < radios.length; r++) {
          var rl = document.createElement('div');
          rl.className = 'scf-debug__nextitem';
          rl.textContent = '• ' + dbgText(radios[r].closest('label') || radios[r]) + ' → ' + (radios[r].getAttribute('data-offer-target') || '?');
          host.appendChild(rl);
        }
      }
      var ctrls = cur.querySelectorAll('[data-cancel-action], [data-cancel-nav]');
      for (var i = 0; i < ctrls.length; i++) {
        var d = dbgDescribe(this, ctrls[i]);
        var row = document.createElement('div');
        row.className = 'scf-debug__nextitem';
        row.textContent = '▸ ' + d.label + '  →  ' + d.to;
        host.appendChild(row);
      }
      if (!ctrls.length) host.textContent = '(no actions on this step)';
    }

    this.dbg.jumps.forEach(function (b) {
      // Result buttons share key "result" — only the one whose variant is
      // actually rendered should read as current.
      var isCurrent = b.dataset.key === 'result'
        ? (this.current === 'result' && b.dataset.variant === this.resultVariant)
        : (b.dataset.key === this.current);
      b.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    }, this);
  };

  Flow.prototype.initDebug = function () {
    var self = this;
    function mk(tag, cls, txt) { var n = document.createElement(tag); if (cls) n.className = cls; if (txt != null) n.textContent = txt; return n; }
    function btn(txt, fn) { var b = mk('button', 'scf-debug__btn', txt); b.type = 'button'; b.addEventListener('click', fn); return b; }
    function row() { return mk('div', 'scf-debug__row'); }
    function label(t) { return mk('div', 'scf-debug__label', t); }

    var panel = mk('div', 'scf-debug');

    var head = mk('div', 'scf-debug__head');
    head.appendChild(mk('strong', null, '🐞 Cancel flow debug'));
    var min = btn('—', function () {
      panel.classList.toggle('is-min');
      min.textContent = panel.classList.contains('is-min') ? '+' : '—';
    });
    min.className = 'scf-debug__min';
    head.appendChild(min);
    panel.appendChild(head);

    var body = mk('div', 'scf-debug__body');
    panel.appendChild(body);

    var status = row();
    status.appendChild(mk('span', null, 'step: '));
    var cur = mk('b', 'scf-debug__cur', '—');
    var pill = mk('span', 'scf-debug__pill', this.lastValidate);
    status.appendChild(cur);
    status.appendChild(mk('span', null, '  validate: '));
    status.appendChild(pill);
    body.appendChild(status);

    var tok = mk('div', 'scf-debug__row scf-debug__muted',
      'token: ' + (this.token ? 'present' : 'missing') + '  ·  return: ' + (this.returnUrl || '—'));
    body.appendChild(tok);

    var mock = row();
    mock.appendChild(label('Mock validate:'));
    mock.appendChild(btn('valid', function () { self.mockValid(); }));
    mock.appendChild(btn('invalid', function () { self.fail('invalid'); }));
    mock.appendChild(btn('error', function () { self.fail('error'); }));
    body.appendChild(mock);

    this.dbg = { cur: cur, pill: pill, jumps: [] };

    var jumps = row();
    jumps.appendChild(label('Jump to step:'));
    Object.keys(this.steps).forEach(function (id) {
      if (id === 'result') return;
      var b = btn(id, function () { self.forceStep(id); });
      b.dataset.key = id;
      jumps.appendChild(b);
      self.dbg.jumps.push(b);
    });
    var resStep = this.steps.result;
    if (resStep) {
      var vs = resStep.querySelectorAll('[data-result-variant]');
      for (var i = 0; i < vs.length; i++) {
        (function (v) {
          var b = btn('result: ' + v, function () { self.setResultVariant(v); self.forceStep('result'); });
          b.dataset.key = 'result';
          b.dataset.variant = v;
          jumps.appendChild(b);
          self.dbg.jumps.push(b);
        })(vs[i].getAttribute('data-result-variant'));
      }
    }
    body.appendChild(jumps);

    var nextWrap = row();
    nextWrap.appendChild(label('Next from this step:'));
    var next = mk('div', 'scf-debug__next');
    nextWrap.appendChild(next);
    body.appendChild(nextWrap);
    this.dbg.next = next;

    var logWrap = row();
    logWrap.appendChild(label('Flow log:'));
    var log = mk('div', 'scf-debug__log');
    logWrap.appendChild(log);
    body.appendChild(logWrap);
    this.dbg.log = log;

    var loopWrap = row();
    loopWrap.appendChild(label('Loop API data (validate · accept · cancel):'));
    var loop = mk('pre', 'scf-debug__loop');
    loopWrap.appendChild(loop);
    body.appendChild(loopWrap);
    this.dbg.loop = loop;
    this.debugLoop();

    document.body.appendChild(panel);
    // Design mode renders every result variant without [hidden], so they stack.
    // Collapse to a single variant up front so debug previews one screen at a
    // time (the "result: …" buttons switch between them).
    if (resStep) {
      var firstVar = resStep.querySelector('[data-result-variant]');
      if (firstVar) this.setResultVariant(firstVar.getAttribute('data-result-variant'));
    }
    this.debugLog('debug on · ' + Object.keys(this.steps).length + ' steps detected');
    this.debugRefresh();
    // Render the per-block plan-id check immediately (catalog-only at first; it
    // re-renders with live validate data once that resolves). Lets you verify ids
    // in the theme editor with no token, using just the List-plans URL.
    if (this.steps['change-plan']) this.debugPlanConfigs();
    if (this.steps['offer-price']) this.debugPriceCards();
  };

  function init() {
    var roots = document.querySelectorAll('[data-subs-cancel]');
    for (var i = 0; i < roots.length; i++) {
      var n = roots[i];
      if (n.__subsCancelInit) continue;
      n.__subsCancelInit = true;
      try { new Flow(n); } catch (e) { if (window.console) console.error('[subs-cancel]', e); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () { init(); });
})();
